import React from "react";
import { Link } from "react-router-dom";
import {
  ReferralPartner,
} from "../../features/admin/models/ReferralPartner";
import { ROUTES } from "../../routes";
import { getDisplayLocale } from "../../features/utils/timezoneUtils";

const formatDate = (date: Date): string =>
  new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(date);

const formatMoney = (minorUnits: number, currency: string, locale: string): string =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: minorUnits % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(minorUnits / 100);

interface ReferralPartnerRowProps {
  partner: ReferralPartner;
}

/** One partner in the admin overview: identity, code, terms state, and totals. */
const ReferralPartnerRow: React.FC<ReferralPartnerRowProps> = ({ partner }) => (
  <div className="with-border flex flex-wrap items-center justify-between gap-3 rounded-2xl p-4 lg:p-6">
    <div className="min-w-0">
      <Link
        to={ROUTES.ADMIN_REFERRAL_DETAILS.replace(":id", partner.id)}
        className="truncate font-semibold text-grey-10 dark:text-white cursor-pointer hover:underline"
      >
        {partner.name}
      </Link>
      <p className="truncate text-sm text-grey-40 dark:text-grey-70">{partner.email}</p>
      <p className="mt-1 text-xs text-grey-50 dark:text-grey-60">
        Code <span className="font-mono">{partner.code}</span> · Created{" "}
        {formatDate(partner.dateCreated)}
      </p>
    </div>
    <div className="flex flex-wrap items-center gap-2">
      {partner.termsAcceptedAt ? (
        <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800 dark:bg-green-900/30 dark:text-green-300">
          Terms accepted
        </span>
      ) : (
        <span className="rounded-full bg-grey-90 px-3 py-1 text-xs font-medium text-grey-30 dark:bg-primary-20 dark:text-grey-70">
          Terms pending
        </span>
      )}
      <span className="rounded-full bg-grey-90 px-3 py-1 text-xs font-medium text-grey-30 dark:bg-primary-20 dark:text-grey-70">
        {partner.referralCount} referred · {partner.convertedCount} converted
      </span>
      {partner.unpaidAmountMinorUnits > 0 && (
        <span className="rounded-full bg-primary-95 px-3 py-1 text-xs font-medium text-primary-40 dark:bg-primary-30 dark:text-primary-90">
          Unpaid {formatMoney(partner.unpaidAmountMinorUnits, partner.bountyCurrency, getDisplayLocale())}
        </span>
      )}
    </div>
  </div>
);

export default ReferralPartnerRow;
